import React, { useState, useMemo } from 'react';
import { Building, Edit, Plus, User, Trash2, AlertTriangle, Clock, CheckCircle, XCircle, Calendar, Trophy, List, Search, RefreshCw, MapPin, UserCheck, UserX, UserMinus, HelpCircle, CreditCard, CircleDot, ParkingCircle, FileSpreadsheet } from 'lucide-react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../api.js';
import { fuelCardAPI, driverBadgeAPI } from '../../api/index.js';
import { handleApiError } from '../../utils/errorHandler.js';
import DriverEditModal from './forms/DriverEditModal.jsx';
import DriverDetailView from './modals/DriverDetailView.jsx';
import DriverLeagueTable from './tables/DriverLeagueTable.jsx';
import DriverAvailabilityModal from './modals/DriverAvailabilityModal.jsx';
import DriverImportModal from './modals/DriverImportModal.jsx';
import MissingEmailDot from './badges/MissingEmailDot.jsx';
import { PoolPresenceBadge, usePoolCustodyIndex } from '../pool/PoolPresenceCard.jsx';
import { isPlaceholderEmail } from '../../utils/driverLicense.js';
import Pagination, { usePagination } from '../common/Pagination.jsx';
import SkeletonLoader from '../common/SkeletonLoader.jsx';

// Temporary imports from index.js
import { useDrivers, useOrganizations, useWorkingSites } from '../../index.js';
import { useSearch } from '../../v3/context/SearchContext.jsx';

// License expiry status helper
function getLicenseExpiryStatus(expiryDate) {
  if (!expiryDate) return { status: 'unknown', label: 'Non renseigné', color: 'gray', daysLeft: null };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry - today;
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    return { status: 'expired', label: 'Expiré', color: 'red', daysLeft };
  } else if (daysLeft <= 30) {
    return { status: 'critical', label: `${daysLeft}j`, color: 'red', daysLeft };
  } else if (daysLeft <= 90) {
    return { status: 'warning', label: `${daysLeft}j`, color: 'orange', daysLeft };
  } else if (daysLeft <= 180) {
    return { status: 'attention', label: `${daysLeft}j`, color: 'yellow', daysLeft };
  } else {
    return { status: 'valid', label: 'Valide', color: 'green', daysLeft };
  }
}

// Availability status helper
const AVAILABILITY_STATUS_CONFIG = {
  AVAILABLE: {
    label: 'Disponible', // repli — l'affichage passe par t('drivers.availability.status.<code>')
    color: 'green',
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    icon: UserCheck
  },
  UNAVAILABLE_PLANNED: {
    label: 'Indisponible (prévu)',
    color: 'yellow',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-300',
    icon: UserMinus
  },
  UNAVAILABLE_UNPLANNED: {
    label: 'Indisponible',
    color: 'red',
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    icon: UserX
  },
  UNKNOWN: {
    label: 'Inconnu',
    color: 'gray',
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-300',
    icon: HelpCircle
  }
};

const AVAILABILITY_REASON_LABELS = {
  NONE: '',
  LEAVE: 'Congé',
  SICK_LEAVE: 'Arrêt maladie',
  TRAINING: 'Formation',
  MEDICAL_VISIT: 'Visite médicale',
  ADMIN: 'Administratif',
  VEHICLE_ISSUE: 'Problème véhicule',
  OTHER: 'Autre'
};

function getAvailabilityConfig(status) {
  return AVAILABILITY_STATUS_CONFIG[status] || AVAILABILITY_STATUS_CONFIG.UNKNOWN;
}

// Availability Badge Component
function AvailabilityBadge({ status, reason, until, compact = false }) {
  const { t } = useTranslation();
  const config = getAvailabilityConfig(status);
  const Icon = config.icon;
  const statusCode = AVAILABILITY_STATUS_CONFIG[status] ? status : 'UNKNOWN';
  const statusLabel = t(`drivers.availability.status.${statusCode}`, { defaultValue: config.label });
  const reasonLabel = reason && AVAILABILITY_REASON_LABELS[reason] ? t(`drivers.availability.reason.${reason}`, { defaultValue: AVAILABILITY_REASON_LABELS[reason] }) : '';

  // Check if availability window is active
  const now = new Date();
  let untilText = '';
  if (until) {
    const untilDate = new Date(until);
    if (untilDate > now) {
      const diffDays = Math.ceil((untilDate - now) / (1000 * 60 * 60 * 24));
      untilText = diffDays === 0 ? t('drivers.availability.untilToday') : t('drivers.availability.remainingDays', { count: diffDays });
    }
  }

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
        title={`${statusLabel}${reasonLabel ? ` - ${reasonLabel}` : ''}${untilText ? ` (${untilText})` : ''}`}
      >
        <Icon className="w-3 h-3" />
        {t(`drivers.availability.short.${statusCode}`)}
      </span>
    );
  }

  return (
    <div className={`inline-flex flex-col items-start gap-0.5`}>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {statusLabel}
      </span>
      {reasonLabel && status !== 'AVAILABLE' && (
        <span className="text-xs text-gray-500 ml-1">{reasonLabel}</span>
      )}
      {untilText && status !== 'AVAILABLE' && (
        <span className="text-xs text-gray-400 ml-1">{untilText}</span>
      )}
    </div>
  );
}

// License Expiry Alert Banner Component
function LicenseExpiryAlertBanner({ drivers }) {
  const { t } = useTranslation();
  const alertDrivers = useMemo(() => {
    return drivers
      .map(d => ({
        ...d,
        expiryStatus: getLicenseExpiryStatus(d.license_expiry)
      }))
      .filter(d => ['expired', 'critical', 'warning'].includes(d.expiryStatus.status))
      .sort((a, b) => (a.expiryStatus.daysLeft || 0) - (b.expiryStatus.daysLeft || 0));
  }, [drivers]);

  const expired = alertDrivers.filter(d => d.expiryStatus.status === 'expired');
  const critical = alertDrivers.filter(d => d.expiryStatus.status === 'critical');
  const warning = alertDrivers.filter(d => d.expiryStatus.status === 'warning');

  if (alertDrivers.length === 0) return null;

  return (
    <div className="space-y-3 mb-6">
      {/* Expired Licenses */}
      {expired.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-red-800">{t('drivers.licenseAlerts.expiredTitle', { count: expired.length })}</h3>
              <p className="text-sm text-red-700 mt-1">
                {t('drivers.licenseAlerts.expiredHint')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {expired.map(d => (
                  <span key={d.id} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                    <User className="w-3 h-3" />
                    {d.first_name} {d.last_name}
                    <span className="text-red-600 font-semibold">
                      ({t('drivers.licenseAlerts.daysPast', { count: Math.abs(d.expiryStatus.daysLeft) })})
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Critical - Less than 30 days */}
      {critical.length > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-orange-800">{t('drivers.licenseAlerts.criticalTitle', { count: critical.length })}</h3>
              <p className="text-sm text-orange-700 mt-1">
                {t('drivers.licenseAlerts.criticalHint')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {critical.map(d => (
                  <span key={d.id} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                    <Clock className="w-3 h-3" />
                    {d.first_name} {d.last_name}
                    <span className="font-semibold">({t('drivers.licenseAlerts.daysShort', { count: d.expiryStatus.daysLeft })})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning - Less than 90 days */}
      {warning.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Calendar className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-yellow-800">{t('drivers.licenseAlerts.warningTitle', { count: warning.length })}</h3>
              <p className="text-sm text-yellow-700 mt-1">
                {t('drivers.licenseAlerts.warningHint')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {warning.map(d => (
                  <span key={d.id} className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                    <Calendar className="w-3 h-3" />
                    {d.first_name} {d.last_name}
                    <span className="font-semibold">({t('drivers.licenseAlerts.daysShort', { count: d.expiryStatus.daysLeft })})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// License Expiry Stats Card
function LicenseExpiryStats({ drivers }) {
  const { t } = useTranslation();
  const stats = useMemo(() => {
    const result = { expired: 0, critical: 0, warning: 0, attention: 0, valid: 0, unknown: 0 };
    drivers.forEach(d => {
      const status = getLicenseExpiryStatus(d.license_expiry).status;
      result[status]++;
    });
    return result;
  }, [drivers]);

  return (
    <div className="grid grid-cols-6 gap-3 mb-6">
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-red-500">
        <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
        <div className="text-xs text-gray-500">{t('drivers.licenseStatus.expired')}</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-orange-500">
        <div className="text-2xl font-bold text-orange-600">{stats.critical}</div>
        <div className="text-xs text-gray-500">{t('drivers.licenseStatus.under30')}</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-yellow-500">
        <div className="text-2xl font-bold text-yellow-600">{stats.warning}</div>
        <div className="text-xs text-gray-500">{t('drivers.licenseStatus.under90')}</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-blue-500">
        <div className="text-2xl font-bold text-blue-600">{stats.attention}</div>
        <div className="text-xs text-gray-500">{t('drivers.licenseStatus.under180')}</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-green-500">
        <div className="text-2xl font-bold text-green-600">{stats.valid}</div>
        <div className="text-xs text-gray-500">{t('drivers.licenseStatus.valid')}</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-gray-400">
        <div className="text-2xl font-bold text-gray-600">{stats.unknown}</div>
        <div className="text-xs text-gray-500">{t('drivers.licenseStatus.unknown')}</div>
      </div>
    </div>
  );
}

function DriversView({ onMessage, autoSelectId, returnTo, returnParams, onNavigate }) {
  const { t } = useTranslation();
  // Use React Query hooks for data fetching
  const queryClient = useQueryClient();
  const { data: driversRaw = [], isLoading: driversLoading, error: driversError } = useDrivers();
  const { data: organizationsRaw = [] } = useOrganizations();
  const { data: sitesRaw = [] } = useWorkingSites();
  // Defensive: ensure arrays even if API returns null/object
  const drivers = Array.isArray(driversRaw) ? driversRaw : [];
  const organizations = Array.isArray(organizationsRaw) ? organizationsRaw : [];
  const sites = Array.isArray(sitesRaw) ? sitesRaw : [];

  // Helper to get site name by ID
  const getSiteName = (siteId) => {
    if (!siteId) return null;
    const site = sites.find(s => s.id === parseInt(siteId));
    return site ? site.site_name : null;
  };

  // Gardes en cours, en UNE lecture pour toute la liste : jamais un appel par
  // ligne. Silencieux si le module pool n'est pas là.
  const { byDriver: custodyByDriver } = usePoolCustodyIndex();

  // Fetch risk profiles for league table (using rankings endpoint for dynamic claims calculation)
  const { data: riskProfilesResult = { data: [] } } = useQuery({
    queryKey: ['riskProfiles', 'rankings'],
    queryFn: () => api.riskProfile.getRankings(),
    staleTime: 60000, // 1 minute
  });
  const riskProfiles = riskProfilesResult.data || [];

  // Fetch equipment (fuel cards + badges) for indicator column
  const { data: fuelCardsResult = { data: [] } } = useQuery({
    queryKey: ['fuelCards', 'all'],
    queryFn: () => fuelCardAPI.getAll(),
    staleTime: 60000,
  });
  const { data: driverBadgesResult = { data: [] } } = useQuery({
    queryKey: ['driverBadges', 'all'],
    queryFn: () => driverBadgeAPI.getAll(),
    staleTime: 60000,
  });

  // Build driver equipment lookup: { driverId: { fuelCard, toll, parking } }
  const driverEquipmentMap = useMemo(() => {
    const map = {};
    const allCards = fuelCardsResult.data || [];
    const allBadges = driverBadgesResult.data || [];

    allCards.forEach(card => {
      if (!card.driver_id) return;
      if (!map[card.driver_id]) map[card.driver_id] = {};
      map[card.driver_id].fuelCard = card;
    });
    allBadges.forEach(badge => {
      if (!badge.driver_id) return;
      if (!map[badge.driver_id]) map[badge.driver_id] = {};
      if (badge.badge_type === 'toll') map[badge.driver_id].toll = badge;
      if (badge.badge_type === 'parking') map[badge.driver_id].parking = badge;
    });
    return map;
  }, [fuelCardsResult.data, driverBadgesResult.data]);

  // UI state (not server data)
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'league'
  const [editingDriver, setEditingDriver] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [availabilityDriver, setAvailabilityDriver] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [orgFilter, setOrgFilter] = useState(''); // '' = all orgs
  const [siteFilter, setSiteFilter] = useState(''); // '' = all sites
  const [expiryFilter, setExpiryFilter] = useState('all'); // 'all', 'expired', 'critical', 'warning', 'valid'
  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // 'all', 'AVAILABLE', 'UNAVAILABLE_PLANNED', 'UNAVAILABLE_UNPLANNED', 'UNKNOWN'
  const { searchQuery } = useSearch();

  // Handle loading state
  const loading = driversLoading;

  // Auto-select driver from search/navigation
  React.useEffect(() => {
    if (autoSelectId && drivers.length > 0) {
      const driver = drivers.find(d => d.id === autoSelectId);
      if (driver) {
        setSelectedDriver(driver);
        setShowDetailView(true);
      }
    }
  }, [autoSelectId, drivers]);

  // Handle errors
  if (driversError) {
    onMessage && onMessage({
      title: t('drivers.messages.errorTitle'),
      message: t('drivers.messages.loadError'),
      type: 'error'
    });
  }

  const handleEdit = (driver) => {
    setEditingDriver(driver);
  };

  const handleAdd = () => {
    setEditingDriver({});  // Empty object to trigger modal in "add" mode
  };

  const handleCloseEdit = () => {
    setEditingDriver(null);
  };

  const handleSuccess = async () => {
    // Invalidate and refetch drivers query to ensure immediate update
    await queryClient.invalidateQueries({ queryKey: ['drivers'] });
    await queryClient.refetchQueries({ queryKey: ['drivers'] });
    setEditingDriver(null);
  };

  const handleViewDetails = (driver) => {
    setSelectedDriver(driver);
    setShowDetailView(true);
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedDriver(null);
    // Navigate back to returnTo view if specified, with optional params to restore context
    if (returnTo && onNavigate) {
      onNavigate(returnTo, returnParams || {});
    }
  };

  const handleDetailViewUpdate = async () => {
    // Invalidate and refetch drivers
    await queryClient.invalidateQueries({ queryKey: ['drivers'] });
    queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    queryClient.invalidateQueries({ queryKey: ['trips'] });
    queryClient.invalidateQueries({ queryKey: ['workingSites'] });
    queryClient.invalidateQueries({ queryKey: ['fuelCards'] });
    queryClient.invalidateQueries({ queryKey: ['driverBadges'] });

    // Fetch fresh driver data directly to update selectedDriver
    if (selectedDriver) {
      try {
        const response = await api.driver.getById(selectedDriver.id);
        // Handle both { data: driver } and direct driver response
        const freshDriver = response?.data || response;
        if (freshDriver && freshDriver.first_name) {
          setSelectedDriver(freshDriver);
        }
      } catch (e) {
        console.error('Failed to refresh driver:', e);
      }
    }
  };

  const handleDelete = async (driver, e) => {
    e.stopPropagation();

    const confirmed = window.confirm(
      t('drivers.messages.deleteConfirm', { name: `${driver.first_name} ${driver.last_name}` })
    );

    if (!confirmed) return;

    try {
      await api.driver.delete(driver.id);

      onMessage && onMessage({
        title: t('drivers.messages.successTitle'),
        message: t('drivers.messages.deleteSuccess', { name: `${driver.first_name} ${driver.last_name}` }),
        type: 'success'
      });

      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    } catch (error) {
      handleApiError(error, 'delete', { onMessage });
    }
  };

  // Refresh handler
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['drivers'] });
    queryClient.invalidateQueries({ queryKey: ['riskProfiles'] });
  };

  // Filter drivers by search, organization, status, and expiry status
  const filteredDrivers = useMemo(() => {
    return drivers.filter(d => {
      // By default, exclude inactive/terminated drivers (archived)
      if (d.status === 'inactive' || d.status === 'terminated') return false;

      // Search filter
      if (searchQuery && searchQuery.length >= 2) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          d.first_name?.toLowerCase().includes(query) ||
          d.last_name?.toLowerCase().includes(query) ||
          d.email?.toLowerCase().includes(query) ||
          d.phone?.toLowerCase().includes(query) ||
          d.license_number?.toLowerCase().includes(query) ||
          d.org_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Org filter
      if (orgFilter && d.org_code !== orgFilter) return false;

      // Site filter
      if (siteFilter) {
        if (siteFilter === 'none') {
          if (d.working_site_id) return false;
        } else if (String(d.working_site_id) !== siteFilter) {
          return false;
        }
      }

      // Expiry filter
      if (expiryFilter !== 'all') {
        const status = getLicenseExpiryStatus(d.license_expiry).status;
        if (expiryFilter === 'alerts' && !['expired', 'critical', 'warning'].includes(status)) return false;
        if (expiryFilter !== 'alerts' && status !== expiryFilter) return false;
      }

      // Availability filter
      if (availabilityFilter !== 'all') {
        const avail = d.availability_status || 'UNKNOWN';
        if (availabilityFilter === 'unavailable') {
          if (!['UNAVAILABLE_PLANNED', 'UNAVAILABLE_UNPLANNED'].includes(avail)) return false;
        } else if (avail !== availabilityFilter) {
          return false;
        }
      }

      return true;
    });
  }, [drivers, searchQuery, orgFilter, siteFilter, expiryFilter, availabilityFilter]);

  const { currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, paginateArray } = usePagination(filteredDrivers.length, 1, 25);

  // Active drivers (excludes archived) - for stats cards
  const activeDrivers = useMemo(() => {
    return drivers.filter(d => d.status !== 'inactive' && d.status !== 'terminated');
  }, [drivers]);

  // Calculate stats for header
  const stats = useMemo(() => {
    const result = {
      total: 0, active: 0, interim: 0, cdd: 0, expired: 0, critical: 0,
      available: 0, unavailablePlanned: 0, unavailableUnplanned: 0, unknown: 0
    };
    drivers.forEach(d => {
      if (d.status === 'inactive' || d.status === 'terminated') return;
      result.total++;
      if (d.status === 'active') result.active++;
      else if (d.status === 'interim') result.interim++;
      else if (d.status === 'temporary') result.cdd++;

      const expStatus = getLicenseExpiryStatus(d.license_expiry).status;
      if (expStatus === 'expired') result.expired++;
      else if (expStatus === 'critical') result.critical++;

      // Availability stats
      const avail = d.availability_status || 'UNKNOWN';
      if (avail === 'AVAILABLE') result.available++;
      else if (avail === 'UNAVAILABLE_PLANNED') result.unavailablePlanned++;
      else if (avail === 'UNAVAILABLE_UNPLANNED') result.unavailableUnplanned++;
      else result.unknown++;
    });
    return result;
  }, [drivers]);

  // Loading state
  if (loading) {
    return <SkeletonLoader type="table" rows={8} columns={10} />;
  }

  return (
    <div className="space-y-4">
      <DriverEditModal
        isOpen={!!editingDriver}
        onClose={handleCloseEdit}
        driver={editingDriver}
        onSuccess={handleSuccess}
        onMessage={onMessage}
      />

      <DriverAvailabilityModal
        isOpen={!!availabilityDriver}
        onClose={() => setAvailabilityDriver(null)}
        driver={availabilityDriver}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ['drivers'] });
        }}
        onMessage={onMessage}
      />

      <DriverImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ['drivers'] });
        }}
        onMessage={onMessage}
      />

      {showDetailView && (
        <DriverDetailView
          driver={selectedDriver}
          onClose={handleCloseDetailView}
          onMessage={onMessage}
          onUpdate={handleDetailViewUpdate}
        />
      )}

      {/* Header Card with Stats, Search, and Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <User className="w-6 h-6 text-amber-600" />
              {t('drivers.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{stats.total}</span> {t('drivers.stats.driversWord', { count: stats.total })}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{stats.active} {t('drivers.status.active')}</span>
              <span className="text-gray-300">|</span>
              <span className="text-blue-600">{stats.interim} {t('drivers.status.interim')}</span>
              {stats.cdd > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-gray-600">{stats.cdd} {t('drivers.status.temporary')}</span>
                </>
              )}
              {stats.expired > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-red-600 font-semibold">{t('drivers.stats.expiredLicenses', { count: stats.expired })}</span>
                </>
              )}
              {stats.critical > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-orange-600">{t('drivers.stats.imminentExpiry', { count: stats.critical })}</span>
                </>
              )}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{stats.available} {t('drivers.stats.availableShort')}</span>
              {(stats.unavailablePlanned + stats.unavailableUnplanned) > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-yellow-600">{stats.unavailablePlanned + stats.unavailableUnplanned} {t('drivers.stats.unavailableShort')}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              data-action="import-drivers"
              className="px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 flex items-center gap-2 shadow-sm"
            >
              <FileSpreadsheet className="w-5 h-5" />
              {t('drivers.actions.import')}
            </button>
            <button
              onClick={handleAdd}
              data-action="add-driver"
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 shadow-sm"
            >
              <Plus className="w-5 h-5" />
              {t('drivers.actions.add')}
            </button>
          </div>
        </div>

        {/* Refresh */}
        <div className="flex gap-4 mb-4 justify-end">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={() => setActiveTab('list')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'list'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <List className="w-5 h-5" />
            {t('drivers.tabs.list')}
          </button>
          <button
            onClick={() => setActiveTab('league')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'league'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Trophy className="w-5 h-5" />
            {t('drivers.tabs.league')}
          </button>
        </div>
      </div>

      {activeTab === 'list' ? (
        <>
          {/* Filters Bar */}
          <div className="flex items-center gap-4 flex-wrap bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">{t('drivers.filters.organization')}</label>
              <select
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              >
                <option value="">{t('drivers.filters.allFeminine')}</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.code}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">{t('drivers.filters.site')}</label>
              <select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              >
                <option value="">{t('drivers.filters.all')}</option>
                <option value="none">{t('drivers.filters.noSite')}</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.site_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">{t('drivers.filters.licenseStatus')}</label>
              <select
                value={expiryFilter}
                onChange={(e) => setExpiryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              >
                <option value="all">{t('drivers.filters.all')}</option>
                <option value="alerts">{t('drivers.filters.alertsOnly')}</option>
                <option value="expired">{t('drivers.licenseStatus.expired')}</option>
                <option value="critical">{t('drivers.licenseStatus.under30')}</option>
                <option value="warning">{t('drivers.licenseStatus.under90')}</option>
                <option value="valid">{t('drivers.licenseStatus.valid')}</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">{t('drivers.filters.availability')}</label>
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              >
                <option value="all">{t('drivers.filters.all')}</option>
                <option value="AVAILABLE">{t('drivers.filters.available')}</option>
                <option value="unavailable">{t('drivers.filters.unavailable')}</option>
                <option value="UNAVAILABLE_PLANNED">{t('drivers.filters.unavailablePlanned')}</option>
                <option value="UNAVAILABLE_UNPLANNED">{t('drivers.filters.unavailableUnplanned')}</option>
                <option value="UNKNOWN">{t('drivers.availability.status.UNKNOWN')}</option>
              </select>
            </div>

            {(orgFilter || siteFilter || expiryFilter !== 'all' || availabilityFilter !== 'all') && (
              <button
                onClick={() => { setOrgFilter(''); setSiteFilter(''); setExpiryFilter('all'); setAvailabilityFilter('all'); }}
                className="text-sm text-amber-600 hover:text-amber-700 underline"
              >
                {t('common.actions.resetFilters')}
              </button>
            )}

            <div className="flex-1"></div>

            <span className="text-sm text-gray-600">
              {t('drivers.list.shownCount', { count: filteredDrivers.length })}
            </span>
          </div>

          {filteredDrivers.length === 0 ? (
            <div className="bg-white p-12 rounded-lg shadow-md text-center">
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {searchQuery || orgFilter || siteFilter || expiryFilter !== 'all' ? t('common.states.noResult') : t('common.emptyState.drivers.title')}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || orgFilter || siteFilter || expiryFilter !== 'all'
                  ? t('drivers.list.emptyFilteredHint')
                  : t('drivers.list.emptyHint')}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.driver')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.organization')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.site')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.availability')}</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.equipment')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.categories')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.licenseNumber')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.licenseExpiry')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('drivers.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
              {paginateArray(filteredDrivers).map(driver => {
                const expiryStatus = getLicenseExpiryStatus(driver.license_expiry);

                return (
                  <tr
                    key={driver.id}
                    onClick={() => handleViewDetails(driver)}
                    className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                      expiryStatus.status === 'expired' ? 'bg-red-50' :
                      expiryStatus.status === 'critical' ? 'bg-orange-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {expiryStatus.status === 'expired' && (
                          <XCircle className="w-5 h-5 text-red-500" title={t('drivers.licenseStatus.expiredOne')} />
                        )}
                        {expiryStatus.status === 'critical' && (
                          <AlertTriangle className="w-5 h-5 text-orange-500" title={t('drivers.licenseStatus.imminent')} />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{driver.first_name} {driver.last_name}</span>
                            <PoolPresenceBadge kind="driver" custody={custodyByDriver.get(String(driver.id))} />
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1.5">
                            <MissingEmailDot email={driver.email} />
                            {isPlaceholderEmail(driver.email)
                              ? <span className="italic text-gray-400">{t('drivers.missingEmail.label')}</span>
                              : driver.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {driver.org_name || <span className="text-gray-400 italic">{t('drivers.list.unassigned')}</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-purple-400" />
                        <span className="text-sm text-gray-900">
                          {getSiteName(driver.working_site_id) || <span className="text-gray-400 italic">-</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        driver.status === 'active' ? 'bg-green-100 text-green-800' :
                        driver.status === 'temporary' ? 'bg-amber-100 text-amber-800' :
                        driver.status === 'interim' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {t(`drivers.status.${driver.status}`, { defaultValue: driver.status })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAvailabilityDriver(driver);
                        }}
                        className="hover:opacity-80 transition-opacity"
                        title={t('drivers.actions.editAvailability')}
                      >
                        <AvailabilityBadge
                          status={driver.availability_status}
                          reason={driver.availability_reason}
                          until={driver.availability_until}
                          compact
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const eq = driverEquipmentMap[driver.id];
                        if (!eq) return <span className="text-gray-300 text-xs">—</span>;
                        const isExpiringSoon = (item) => item?.expiry_date && new Date(item.expiry_date) < new Date(Date.now() + 30 * 86400000);
                        const isExpired = (item) => item?.expiry_date && new Date(item.expiry_date) < new Date();
                        const getColor = (item) => !item ? null : isExpired(item) || item.status === 'blocked' ? 'text-red-500' : isExpiringSoon(item) ? 'text-orange-500' : 'text-green-500';
                        return (
                          <div className="flex items-center justify-center gap-1.5">
                            {eq.fuelCard && (
                              <CreditCard className={`w-4 h-4 ${getColor(eq.fuelCard)}`} title={`${t('drivers.equipment.fuelCard')}: ${eq.fuelCard.card_number}`} />
                            )}
                            {eq.toll && (
                              <CircleDot className={`w-4 h-4 ${getColor(eq.toll)}`} title={`${t('drivers.equipment.toll')}: ${eq.toll.badge_number}`} />
                            )}
                            {eq.parking && (
                              <ParkingCircle className={`w-4 h-4 ${getColor(eq.parking)}`} title={`${t('drivers.equipment.parking')}: ${eq.parking.badge_number}`} />
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 flex-wrap">
                        {driver.license_category_b ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">B</span>
                        ) : null}
                        {driver.license_category_c ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">C</span>
                        ) : null}
                        {driver.license_category_d ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">D</span>
                        ) : null}
                        {driver.license_category_ce ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">CE</span>
                        ) : null}
                        {!driver.license_category_b && !driver.license_category_c && !driver.license_category_d && !driver.license_category_ce ? (
                          <span className="text-xs text-gray-400 italic">{t('drivers.list.noCategory')}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-mono text-gray-700">
                        {driver.license_number || <span className="text-gray-400 italic">-</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {expiryStatus.status === 'valid' && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                        {expiryStatus.status === 'expired' && (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        {expiryStatus.status === 'critical' && (
                          <AlertTriangle className="w-4 h-4 text-orange-500" />
                        )}
                        {expiryStatus.status === 'warning' && (
                          <Clock className="w-4 h-4 text-yellow-600" />
                        )}
                        {expiryStatus.status === 'attention' && (
                          <Clock className="w-4 h-4 text-blue-500" />
                        )}
                        <div>
                          <div className="text-sm text-gray-900">
                            {driver.license_expiry || <span className="text-gray-400 italic">{t('drivers.licenseStatus.unknown')}</span>}
                          </div>
                          {expiryStatus.daysLeft !== null && (
                            <span className={`text-xs font-semibold ${
                              expiryStatus.color === 'red' ? 'text-red-600' :
                              expiryStatus.color === 'orange' ? 'text-orange-600' :
                              expiryStatus.color === 'yellow' ? 'text-yellow-600' :
                              expiryStatus.color === 'green' ? 'text-green-600' :
                              'text-gray-500'
                            }`}>
                              {expiryStatus.daysLeft < 0
                                ? t('drivers.licenseStatus.expiredSince', { count: Math.abs(expiryStatus.daysLeft) })
                                : expiryStatus.daysLeft === 0
                                  ? t('drivers.licenseStatus.expiresToday')
                                  : t('drivers.licenseStatus.inDays', { count: expiryStatus.daysLeft })}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(driver); }}
                        className="text-orange-600 hover:text-orange-900 mr-3"
                        title={t('drivers.actions.editDriver')}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(driver, e)}
                        className="text-red-600 hover:text-red-900"
                        title={t('drivers.actions.deleteDriver')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                );
                  })}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                totalItems={filteredDrivers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                className="px-4 py-3 border-t bg-gray-50"
              />
            </div>
          )}
        </>
      ) : (
        /* League Table Tab */
        <DriverLeagueTable
          drivers={filteredDrivers}
          riskProfiles={riskProfiles}
          onViewDriver={handleViewDetails}
        />
      )}
    </div>
  );
}

export default DriversView;
